import { useEffect, useState } from 'react';
import { ADMIN_MESSAGES_CHANGED_EVENT } from '../components/adminMessages/adminMessages';
import { fetchAdminMessagesUnread } from '../services/api';

export const ADMIN_MESSAGES_POLL_MS = 60_000;

/**
 * How many admin messages the Shop has not opened (sidebar badge on Admin messages). Refreshes on
 * mount, every minute while the tab is visible, on window focus and on `admin-messages-changed`.
 * Errors are ignored: the badge keeps its last value.
 */
export function useAdminMessagesUnread(enabled = true): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const res = await fetchAdminMessagesUnread();
        const next = res?.success ? Number(res.data?.unread) : NaN;
        if (!cancelled && Number.isFinite(next)) setCount(Math.max(0, Math.floor(next)));
      } catch {
        /* badge only: keep the last value */
      }
    };

    const onEvent = () => {
      void refresh();
    };
    const onTick = () => {
      if (document.visibilityState === 'hidden') return;
      void refresh();
    };

    void refresh();
    const timer = window.setInterval(onTick, ADMIN_MESSAGES_POLL_MS);
    window.addEventListener('focus', onEvent);
    window.addEventListener(ADMIN_MESSAGES_CHANGED_EVENT, onEvent);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onEvent);
      window.removeEventListener(ADMIN_MESSAGES_CHANGED_EVENT, onEvent);
    };
  }, [enabled]);

  return count;
}
